import { useState, useEffect, useMemo } from 'react';
import { RichNote, CreateRichNoteData, UpdateRichNoteData, RichNotesFilter } from '../types/richNotes';
import { richNotesService } from '../services/richNotesService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useRichNotes = () => {
  const [notes, setNotes] = useState<RichNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const [filters, setFilters] = useState<RichNotesFilter>({
    searchQuery: '',
    category: '',
    tags: [],
    showArchived: false,
    showPinned: false,
    showFavorites: false,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  // Load notes
  const loadNotes = async (includeArchived: boolean = false) => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      setError(null);
      const fetchedNotes = await richNotesService.getNotes(user.uid, includeArchived);
      setNotes(fetchedNotes);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzetek betöltésekor';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Create note
  const createNote = async (noteData: CreateRichNoteData): Promise<RichNote | null> => {
    if (!user?.uid) return null;

    try {
      const newNote = await richNotesService.createNote(user.uid, noteData);
      setNotes(prev => [newNote, ...prev]);
      toast.success('Jegyzet létrehozva');
      return newNote;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet létrehozásakor';
      toast.error(errorMessage);
      return null;
    }
  };

  // Update note
  const updateNote = async (noteId: string, updates: UpdateRichNoteData): Promise<boolean> => {
    try {
      await richNotesService.updateNote(noteId, updates);
      setNotes(prev => prev.map(note => 
        note.id === noteId 
          ? { ...note, ...updates, updatedAt: new Date().toISOString() }
          : note
      ));
      toast.success('Jegyzet frissítve');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet frissítésekor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Delete note
  const deleteNote = async (noteId: string): Promise<boolean> => {
    try {
      await richNotesService.deleteNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast.success('Jegyzet törölve');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet törlésekor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Archive note
  const archiveNote = async (noteId: string): Promise<boolean> => {
    try {
      await richNotesService.archiveNote(noteId);
      setNotes(prev => prev.map(note =>
        note.id === noteId ? { ...note, isArchived: true } : note
      ));
      toast.success('Jegyzet archiválva');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet archiválásakor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Unarchive note
  const unarchiveNote = async (noteId: string): Promise<boolean> => {
    try {
      await richNotesService.unarchiveNote(noteId);
      setNotes(prev => prev.map(note =>
        note.id === noteId ? { ...note, isArchived: false } : note
      ));
      toast.success('Jegyzet visszaállítva');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet visszaállításakor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Toggle pin
  const togglePin = async (noteId: string): Promise<boolean> => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return false;

    try {
      const newPinStatus = !note.isPinned;
      await richNotesService.togglePin(noteId, newPinStatus);
      setNotes(prev => prev.map(n =>
        n.id === noteId ? { ...n, isPinned: newPinStatus } : n
      ));
      toast.success(newPinStatus ? 'Jegyzet rögzítve' : 'Rögzítés feloldva');
      return true;
    } catch (err) {
      toast.error('Hiba történt');
      return false;
    }
  };

  // Toggle favorite
  const toggleFavorite = async (noteId: string): Promise<boolean> => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return false;

    try {
      const newFavoriteStatus = !note.isFavorite;
      await richNotesService.toggleFavorite(noteId, newFavoriteStatus);
      setNotes(prev => prev.map(n =>
        n.id === noteId ? { ...n, isFavorite: newFavoriteStatus } : n
      ));
      toast.success(newFavoriteStatus ? 'Kedvencekhez adva' : 'Kedvencekből eltávolítva');
      return true;
    } catch (err) {
      toast.error('Hiba történt');
      return false;
    }
  };

  // Get all categories
  const categories = useMemo(() => {
    const categoryMap = new Map<string, number>();
    notes.forEach(note => {
      if (!note.isArchived) {
        const count = categoryMap.get(note.category) || 0;
        categoryMap.set(note.category, count + 1);
      }
    });
    return Array.from(categoryMap.entries()).map(([name, count]) => ({
      id: name,
      name,
      count,
      color: '#3B82F6',
    }));
  }, [notes]);

  // Get all tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    notes.forEach(note => {
      if (!note.isArchived) {
        note.tags?.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [notes]);

  // Filter and sort notes
  const filteredNotes = useMemo(() => {
    let filtered = [...notes];

    // Filter by archived status
    if (!filters.showArchived) {
      filtered = filtered.filter(note => !note.isArchived);
    }

    // Filter by pinned
    if (filters.showPinned) {
      filtered = filtered.filter(note => note.isPinned);
    }

    // Filter by favorites
    if (filters.showFavorites) {
      filtered = filtered.filter(note => note.isFavorite);
    }

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(note => note.category === filters.category);
    }

    // Filter by tags
    if (filters.tags.length > 0) {
      filtered = filtered.filter(note =>
        filters.tags.some(tag => note.tags?.includes(tag))
      );
    }

    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(note =>
        note.title.toLowerCase().includes(query) ||
        note.plainText.toLowerCase().includes(query) ||
        note.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[filters.sortBy];
      const bValue = b[filters.sortBy];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return filters.sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return 0;
    });

    // Pinned notes always on top
    return filtered.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return 0;
    });
  }, [notes, filters]);

  // Load notes on mount
  useEffect(() => {
    loadNotes(filters.showArchived);
  }, [user?.uid, filters.showArchived]);

  return {
    notes: filteredNotes,
    allNotes: notes,
    loading,
    error,
    filters,
    setFilters,
    categories,
    allTags,
    createNote,
    updateNote,
    deleteNote,
    archiveNote,
    unarchiveNote,
    togglePin,
    toggleFavorite,
    refreshNotes: () => loadNotes(filters.showArchived),
  };
};
